import { definePreset } from '@primeuix/themes'
import Aura from '@primeuix/themes/aura'
import components from '@/presentation/plugins/presets/components'

// Définition du preset Naboo en étendant Aura avec OKLCH
const NabooPreset = definePreset(Aura, {
  // Personnalisation des couleurs sémantiques
  semantic: {
    primary: {
      50: 'oklch(98% 0.02 240)', // brand-tertiary (très léger)
      100: 'oklch(0.93 0.0216 285.97)', // brand-secondary
      200: 'oklch(88% 0.07 240)',
      300: 'oklch(80% 0.10 240)',
      400: 'oklch(72% 0.14 240)',
      500: 'oklch(65% 0.19 240)', // bo-active
      600: 'oklch(55% 0.21 240)',
      700: 'oklch(45% 0.22 240)',
      800: 'oklch(35% 0.22 240)',
      900: 'oklch(25% 0.20 240)', // brand-primary (bleu Naboo)
      950: 'oklch(15% 0.18 240)'
    },
    colorScheme: {
      light: {
        primary: {
          50: 'oklch(98% 0.02 240)', // brand-tertiary (très léger)
          100: 'oklch(92.7% 0.022 286)', // brand-secondary
          200: 'oklch(88% 0.07 240)',
          300: 'oklch(80% 0.10 240)',
          400: 'oklch(72% 0.14 240)',
          500: 'oklch(65% 0.19 240)', // bo-active
          600: 'oklch(55% 0.21 240)',
          700: 'oklch(45% 0.22 240)',
          800: 'oklch(35% 0.22 240)',
          900: 'oklch(25% 0.20 240)', // brand-primary (bleu Naboo)
          950: 'oklch(15% 0.18 240)'
        },
        // Couleur de surface en mode clair
        surface: {
          0: 'oklch(100% 0 0)', // bo-white
          50: 'oklch(98% 0.009 264.5)', // bo-brand-tertiary
          100: 'oklch(97% 0.02 240)',
          200: 'oklch(92% 0.02 240)',
          300: 'oklch(87% 0.03 240)', // bo-input-border
          400: 'oklch(70% 0.03 240)',
          500: 'oklch(60% 0.03 240)', // bo-secondary
          600: 'oklch(45% 0.03 240)',
          700: 'oklch(35% 0.03 240)',
          800: 'oklch(25% 0.03 240)',
          900: 'oklch(15% 0.03 240)', // bo-primary
          950: 'oklch(10% 0.03 240)'
        }
      },
      dark: {
        // Couleur de surface en mode sombre
        primary: {
          50: 'oklch(25% 0.20 240)', // brand-primary (bleu Naboo)
          100: 'oklch(35% 0.22 240)',
          200: 'oklch(45% 0.22 240)',
          300: 'oklch(55% 0.21 240)',
          400: 'oklch(65% 0.19 240)', // bo-active
          500: 'oklch(72% 0.14 240)',
          600: 'oklch(80% 0.10 240)',
          700: 'oklch(88% 0.07 240)',
          800: 'oklch(95% 0.03 240)', // brand-secondary
          900: 'oklch(98% 0.02 240)', // brand-tertiary (très léger)
          950: 'oklch(99% 0.01 240)'
        },

        surface: {
          0: 'oklch(15% 0.02 240)',
          50: 'oklch(20% 0.02 240)',
          100: 'oklch(25% 0.02 240)',
          200: 'oklch(30% 0.02 240)',
          300: 'oklch(35% 0.02 240)',
          400: 'oklch(40% 0.03 240)',
          500: 'oklch(60% 0.02 240)', // Texte secondaire
          600: 'oklch(60% 0.02 240)',
          700: 'oklch(75% 0.02 240)',
          800: 'oklch(85% 0.02 240)',
          900: 'oklch(85% 0.02 240)', // Texte principal
          950: 'oklch(98% 0.01 240)'
        }
      }
    }
  },
  components: components,

  // Configuration des polices
  font: {
    family: {
      base: '"Open Sans", sans-serif',
      heading: 'Barlow, sans-serif',
      alt: 'Montserrat, sans-serif'
    },
    // Tailles personnalisées
    size: {
      // Tailles personnalisées pour les titres
      h1: '1.8rem',
      h2: '1.6rem',
      h3: '1.4rem',
      h4: '1.3rem',
      h5: '1rem',
      h6: '1rem'
    },
    // Polices spécifiques pour les éléments
    component: {
      // Titres
      'heading.h1': {
        fontFamily: 'Barlow, sans-serif',
        fontWeight: 800,
        fontSize: '1.8rem',
        lineHeight: '36px',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        textAlign: 'center'
      },
      'heading.h2': {
        fontFamily: 'Barlow, sans-serif',
        fontWeight: 700,
        fontSize: '1.6rem',
        lineHeight: '26px',
        letterSpacing: '0.05em',
        textTransform: 'uppercase'
      },
      'heading.h3': {
        fontFamily: 'Barlow, sans-serif',
        fontWeight: 700,
        fontSize: '1.4rem',
        lineHeight: '122%',
        textAlign: 'left'
      },
      'heading.h4': {
        fontFamily: 'Barlow, sans-serif',
        fontWeight: 600,
        fontSize: '1.3rem',
        lineHeight: '138%',
        textAlign: 'left'
      },
      'heading.h5': {
        fontFamily: 'Barlow, sans-serif',
        fontWeight: 600,
        fontSize: '1rem',
        lineHeight: '183%',
        letterSpacing: '0.05em'
      },
      'heading.h6': {
        fontFamily: 'Open Sans, sans-serif',
        fontWeight: 400,
        fontSize: '1rem',
        letterSpacing: '0.05em'
      }
    }
  },

  // Variables globales
  vars: {
    // Variables pour la compatibilité avec les classes existantes
    '--bo-brand': 'var(--primary-900)',
    '--bo-brand-secondary': 'var(--primary-100)',
    '--bo-brand-tertiary': 'var(--primary-50)',
    '--bo-primary': 'var(--surface-900)',
    '--bo-secondary': 'var(--surface-500)',
    '--bo-active': 'var(--primary-500)',
    '--bo-white': 'var(--surface-0)',
    '--bo-table-header': 'var(--surface-50)',
    '--bo-input-border': 'var(--surface-300)',

    // Variable de transition standard
    '--transition-standard':
      'background-color 0.2s, color 0.2s, border-color 0.2s, box-shadow 0.2s, transform 0.3s'
  }
})

export default NabooPreset
